import { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import api, { inr } from "../../lib/api";

const LEVEL_CLS = {
  LOW: "bg-emerald-50 text-emerald-600", MEDIUM: "bg-amber-50 text-amber-700",
  HIGH: "bg-orange-100 text-orange-700", CRITICAL: "bg-red-100 text-red-700",
};

export default function AdminRtoGuard() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/admin/rto-guard").then(({ data }) => setRows(data)).catch(() => {});
  }, []);

  const counts = (rows || []).reduce((acc, r) => { acc[r.risk.level] = (acc[r.risk.level] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="admin-rto-guard-page">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-600 text-white"><ShieldAlert className="h-5 w-5" /></span>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">SHIPANY RTO Guard</h1>
          <p className="text-sm text-slate-500">Predictive return-risk scoring on every active shipment.</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-4 gap-4">
        {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((l) => (
          <div key={l} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`rto-count-${l.toLowerCase()}`}>
            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${LEVEL_CLS[l]}`}>{l}</span>
            <p className="mono mt-2 text-2xl font-bold text-shipany-navy">{counts[l] || 0}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="rto-guard-table">
        <table className="w-full min-w-[960px] text-sm">
          <thead>
            <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
              <th className="px-5 py-3.5">AWB</th><th className="px-4 py-3.5">Customer</th><th className="px-4 py-3.5">Destination</th>
              <th className="px-4 py-3.5">Mode</th><th className="px-4 py-3.5">Score</th><th className="px-4 py-3.5">Risk</th>
              <th className="px-4 py-3.5">Signals</th><th className="px-4 py-3.5">Recommended</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.shipment_id} className="border-t border-slate-100" data-testid={`rto-row-${r.awb}`}>
                <td className="mono px-5 py-3 font-semibold text-shipany-blue">{r.awb}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.customer_email}</td>
                <td className="px-4 py-3 text-xs">{r.destination_city}<span className="block text-[10px] text-slate-400">{r.courier_name}</span></td>
                <td className="px-4 py-3 text-xs">{r.payment_mode}{r.payment_mode === "COD" ? ` ${inr(r.cod_amount)}` : ""}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
                      <div className={`h-full ${r.risk.score >= 65 ? "bg-red-500" : r.risk.score >= 45 ? "bg-orange-400" : r.risk.score >= 25 ? "bg-amber-400" : "bg-emerald-500"}`} style={{ width: `${r.risk.score}%` }} />
                    </div>
                    <span className="mono text-xs font-bold">{r.risk.score}</span>
                  </div>
                </td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${LEVEL_CLS[r.risk.level]}`}>{r.risk.level}</span></td>
                <td className="max-w-52 px-4 py-3 text-[11px] text-slate-500">{r.risk.reasons.join(" · ") || "—"}</td>
                <td className="max-w-52 px-4 py-3 text-[11px] font-medium text-shipany-navy">{r.risk.recommended_action}</td>
              </tr>
            ))}
            {rows && rows.length === 0 && <tr><td colSpan={8} className="px-6 py-10 text-center text-slate-400">No active shipments to score.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
